import { Box, Button } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { ContractProposalBaseInvestmentToolType, type ContractProposalBaseSolutionHorizonType } from "Infrastructure/ApiZfp/Schema";
import type { NsurePlanOverview } from "Infrastructure/ApiZfp/Schema/nsurePlanOverview";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, startTransition, useRef, useState } from "react";
import { InvestmentsNsureWrapper } from "./Forms/ProposedSolution/InvestmentsNsure/InvestmentsNsureWrapper";
import { ProposedSolutionNsureReadOnly } from "./Forms/ProposedSolution/InvestmentsNsure/ProposedSolutionNsureReadOnly";
import { type ProposedSolutionFormHandle, ProposedSolutionFormWrapper } from "./Forms/ProposedSolution/ProposedSolutionFormWrapper";

type Props = {
  onClose: () => void;
  solutionId?: string;
  nsurePlan?: NsurePlanOverview;
  investmentToolType: ContractProposalBaseInvestmentToolType;
  horizonType: ContractProposalBaseSolutionHorizonType;
  isNsure?: boolean;
  readonly open: boolean;
};

export type SubmitProposedSolutionFormHandle = {
  readonly submitForm: () => void;
  readonly deleteSolution?: () => void;
};

export const ProposedSolutionDrawer: FC<Props> = ({ onClose, horizonType, solutionId, nsurePlan, investmentToolType, isNsure, open }) => {
  const { t } = useResource();
  const proposedSolutionFormRef = useRef<SubmitProposedSolutionFormHandle & ProposedSolutionFormHandle>(null);

  const [contentReady, setContentReady] = useState(false);
  const [currentFormType, setCurrentFormType] = useState<ContractProposalBaseInvestmentToolType>(investmentToolType);
  const isEdit = !!solutionId;
  const isNsureCandidate = isNsure ? true : currentFormType === ContractProposalBaseInvestmentToolType.investment && !isEdit;

  // Edge case: the proposal carries a portfolio, but its horizon has no Nsure plan any more. The Nsure flow
  // cannot be loaded without the plan, so only a read-only detail with a delete option is offered.
  const mode: "nsure" | "nsureReadOnly" | "standard" =
    isNsureCandidate && nsurePlan ? "nsure" : isNsure && solutionId ? "nsureReadOnly" : "standard";

  const handleClose = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === "function") active.blur();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      closeLabel={t(Resources.Common.Close)}
      title={
        mode === "nsureReadOnly"
          ? t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.ReadOnly.Title)
          : isEdit
            ? t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionEdit)
            : t(Resources.AnalysisDetail.GoalsFulfillment.Proposed)
      }
      width={mode === "nsure" ? 1280 : undefined}
      customLayout={mode !== "standard"}
      onEntered={() => setContentReady(true)}
      onExited={() => setContentReady(false)}
      startAction={
        isEdit && (
          <Button variant="text" onClick={() => proposedSolutionFormRef.current?.deleteSolution?.()} color="error">
            {t(Resources.Common.Delete)}
          </Button>
        )
      }
      primaryAction={
        <Button variant="contained" type="submit" onClick={() => proposedSolutionFormRef.current?.submitForm()}>
          {isEdit ? t(Resources.Common.Save) : t(Resources.Common.Create)}
        </Button>
      }
    >
      {mode === "nsure" && nsurePlan ? (
        contentReady && (
          <InvestmentsNsureWrapper
            nsurePlan={nsurePlan}
            onFormTypeChange={setCurrentFormType}
            onClose={handleClose}
            horizonType={horizonType}
            edit={isEdit}
            proposedSolutionId={solutionId}
          />
        )
      ) : mode === "nsureReadOnly" && solutionId ? (
        contentReady && <ProposedSolutionNsureReadOnly proposedSolutionId={solutionId} onClose={handleClose} />
      ) : (
        <Box height="100%">
          {contentReady && (
            <ProposedSolutionFormWrapper
              ref={proposedSolutionFormRef}
              edit={isEdit}
              proposedSolutionID={solutionId}
              onSuccess={handleClose}
              onFormTypeChange={(type) => startTransition(() => setCurrentFormType(type))}
              initialFormType={currentFormType}
              horizonType={horizonType}
            />
          )}
        </Box>
      )}
    </Drawer>
  );
};
